import React, { useEffect, useState } from 'react';
import Parse from 'parse';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './ConcentrationPlot.css';

export default function ConcentrationPlot({ machine }) {
  const [data, setData] = useState([]);
  const [yField, setYField] = useState('concentration');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchMeasurements = async () => {
    const Measurement = Parse.Object.extend('Measurement');
    const query = new Parse.Query(Measurement);
    query.equalTo('machine', machine);
    if (fromDate) query.greaterThanOrEqualTo('timestamp', new Date(fromDate));
    if (toDate) {
      const endDate = new Date(toDate);
      // Ensure we include the entire day if it's a day-based query
      if (endDate.getHours() === 23 && endDate.getMinutes() === 59) {
        endDate.setMilliseconds(999);
      }
      query.lessThanOrEqualTo('timestamp', endDate);
    }
    query.ascending('timestamp');
    query.limit(1000);
    
    try {
      const results = await query.find();
      const formatted = results.map((m) => ({
        timestamp: new Date(m.get('timestamp')).toISOString().slice(0, 16),
        concentration: m.get('concentration'),
        particle_count: m.get('particle_count'),
        object_count: m.get('object_count'),
      }));
      setData(formatted);
    } catch (error) {
      console.error('Error fetching measurements:', error);
      setData([]);
    }
};

  useEffect(() => {
    if (machine) {
      fetchMeasurements();
    }
  }, [machine, fromDate, toDate]);

  const exportCSV = () => {
    if (!data || data.length === 0) return;

    const header = ['timestamp', 'concentration (mg/L)', 'particle_count (-)', 'object_count (-)'];
    const rows = data.map((row) => [
      row.timestamp,
      row.concentration,
      row.particle_count,
      row.object_count,
    ]);

    const csvContent = [header, ...rows]
      .map((r) => r.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `microdeep_full_metrics.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPNG = () => {
    const chart = document.querySelector('#chart-wrapper svg');
    const svgData = new XMLSerializer().serializeToString(chart);
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      const imgURL = canvas.toDataURL('image/png');
      const dlLink = document.createElement('a');
      dlLink.download = `microdeep_${yField}.png`;
      dlLink.href = imgURL;
      dlLink.click();
    };
    img.src = url;
  };

  const now = new Date();

  const formatLocalDateTime = (date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16); // Removes the trailing Z and keeps local datetime

const setPresetRange = (daysAgoStart = 0, daysAgoEnd = 0, wholeDay = false) => {
  const from = new Date();
  const to = new Date();

  // Set the dates first
  from.setDate(from.getDate() - daysAgoStart);
  to.setDate(to.getDate() - daysAgoEnd);

  if (wholeDay) {
    // For "Today" button
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
  } else {
    // For "This Week" and "Last 30 Days" buttons
    from.setHours(0, 0, 0, 0);  // Start from beginning of the first day
    to.setHours(23, 59, 59, 999);
  }

  setFromDate(formatLocalDateTime(from));
  setToDate(formatLocalDateTime(to));
};

return (
  <div className="glass-panel">
    <h3>{yField.replace('_', ' ')} over Time</h3>

    <div style={{ marginBottom: 16 }}>
      <select
        value={yField}
        onChange={(e) => setYField(e.target.value)}
        className="control-dropdown"
      >
        <option value="concentration">Concentration</option>
        <option value="particle_count">Particle Count</option>
        <option value="object_count">Object Count</option>
      </select>
    </div>

    <div style={{ marginBottom: 16 }}>
      <button onClick={() => setPresetRange(0, 0, true)} className="orange-button">Today</button>
      <button onClick={() => setPresetRange(7, 0)} className="orange-button">This Week</button>
      <button onClick={() => setPresetRange(30, 0)} className="orange-button">Last 30 Days</button>
    </div>

    <div style={{ marginBottom: 16 }}>
      <input type="datetime-local" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="control-input" />
      <input type="datetime-local" value={toDate} onChange={(e) => setToDate(e.target.value)} className="control-input" />
    </div>

    <div id="chart-wrapper">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid stroke="#ccc" />
          <XAxis dataKey="timestamp" />
          <YAxis />
          <Tooltip
            formatter={(value, name) => {
              const units = {
                concentration: 'mg/L',
                particle_count: 'count/mL',
                object_count: 'count/mL',
              };
              return [`${value}`, `${name} (${units[name] || ''})`];
            }}
          />
          <Line type="monotone" dataKey={yField} stroke="#d46d2a" strokeWidth={3} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>

    <div className="button-row">
      <button onClick={exportCSV} className="orange-button">📄 Export CSV</button>
      <button onClick={exportPNG} className="orange-button">🖼 Export PNG</button>
    </div>
  </div>
);
}